/** One turn: the question, or the answer with everything attached to it. */

import { useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { Message } from "../../shared/types.ts";
import type {
  AgentId,
  AgentState,
  FollowUp,
  LessonPlan,
  Review,
} from "../../shared/agents.ts";
import { Markdown } from "../markdown/Markdown.tsx";
import { AgentRail } from "./AgentRail.tsx";
import { ReviewPanel } from "./ReviewPanel.tsx";
import { PlanCard } from "./PlanCard.tsx";
import { FollowUps } from "./FollowUps.tsx";
import { AnswerOutline, hasOutline, useOutline } from "./AnswerOutline.tsx";
import { StickyHeading } from "./StickyHeading.tsx";
import { AnswerActions } from "./AnswerActions.tsx";
import { SelectionAsk } from "./SelectionAsk.tsx";
import { useEquations } from "../markdown/useEquations.ts";
import { useHighlights } from "../markdown/useHighlights.ts";
import type { Highlight } from "../../shared/highlights.ts";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible.tsx";
import { Badge } from "./ui/badge.tsx";

interface AssistantExtras {
  readonly agents?: Readonly<Record<AgentId, AgentState>>;
  readonly agentDetail?: Readonly<Partial<Record<AgentId, string>>>;
  readonly plan?: LessonPlan | null;
  readonly review?: Review | null;
  readonly followUps?: readonly FollowUp[] | null;
  readonly reasoning?: string | null;
  readonly streaming?: boolean;
  /** Sending a follow-up; absent on a read-only render. */
  readonly onAsk?: (prompt: string) => void;
  readonly busy?: boolean;
  readonly highlights?: readonly Highlight[];
  readonly onHighlight?: (quote: string) => void;
}

/**
 * Every turn opens with a hidden heading.
 *
 * Nothing visible needs one — the margin rule and the spacing already separate
 * the turns for a sighted reader. But a screen reader navigating a long
 * transcript by heading has no other way to move question-to-question, and
 * without them the only headings in the document are whatever the answer's own
 * markdown happened to emit.
 */
export function UserMessage({ message }: { message: Message }) {
  return (
    <article className="mb-12">
      <h2 className="sr-only">Question</h2>
      <div className="rounded-r-lg border-l-2 border-border-strong bg-muted px-4 py-3 text-secondary-foreground [&_.prose>:first-child]:mt-0 [&_.prose>:last-child]:mb-0">
        <Markdown content={message.content} />
      </div>
    </article>
  );
}

interface AssistantMessageProps extends AssistantExtras {
  readonly content: string;
}

export function AssistantMessage({
  content,
  agents,
  agentDetail,
  plan,
  review,
  followUps,
  reasoning,
  streaming = false,
  onAsk,
  busy = false,
  highlights = [],
  onHighlight,
}: AssistantMessageProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { entries, activeId } = useOutline(content, bodyRef);

  // Numbering, references and click-to-copy are applied to the finished answer;
  // renumbering on every streamed token would flicker.
  useEquations(bodyRef, content, !streaming);
  useHighlights(bodyRef, highlights, !streaming);

  return (
    <article className="mb-12">
      <h2 className="sr-only">Answer</h2>

      {agents && streaming && (
        <AgentRail agents={agents} detail={agentDetail ?? {}} />
      )}

      {plan && <PlanCard plan={plan} />}

      {reasoning && <ReasoningPanel reasoning={reasoning} streaming={streaming} />}

      {/* Only once the answer has finished: an outline that grows a section at
          a time under the cursor is a distraction, not navigation. */}
      {!streaming && content.length > 0 && (
        <AnswerOutline entries={entries} activeId={activeId} bodyRef={bodyRef} />
      )}

      {!streaming && hasOutline(entries) && (
        <StickyHeading entries={entries} activeId={activeId} />
      )}

      <div ref={bodyRef}>
        {content.length > 0 ? (
          <Markdown content={content} complete={!streaming} />
        ) : (
          <ThinkingIndicator />
        )}
        {streaming && content.length > 0 && <Caret />}
      </div>

      {onAsk && !streaming && content.length > 0 && (
        <>
          <AnswerActions content={content} />
          <SelectionAsk
            containerRef={bodyRef}
            onAsk={onAsk}
            {...(onHighlight ? { onHighlight } : {})}
            disabled={busy}
          />
        </>
      )}

      {review && <ReviewPanel review={review} />}

      {followUps && followUps.length > 0 && onAsk && (
        <FollowUps followUps={followUps} onPick={onAsk} disabled={busy} />
      )}
    </article>
  );
}

/** The model's chain of thought — collapsed by default; it is not the answer. */
function ReasoningPanel({
  reasoning,
  streaming,
}: {
  reasoning: string;
  streaming: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-4 font-sans">
      <CollapsibleTrigger className="group/reasoning flex w-full items-center gap-2 rounded-md py-1 text-sm text-muted-foreground transition-colors hover:text-secondary-foreground">
        <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/reasoning:rotate-90" />
        Working{streaming ? "…" : ""}
        <Badge variant="secondary" className="font-normal">
          {wordCount(reasoning)} words
        </Badge>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <pre className="mt-2 max-h-96 overflow-auto rounded-lg border bg-muted p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-secondary-foreground">
          {reasoning}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Marks the live edge of a streaming answer. Reduced motion keeps the bar and
 * drops only the blink — it still says "the text stops here", which is its job.
 */
function Caret() {
  return (
    <span
      aria-hidden="true"
      className="ml-0.5 inline-block h-[1.1em] w-0.5 animate-caret-blink bg-primary align-text-bottom motion-reduce:animate-none"
    />
  );
}

/**
 * Three dots, while the first token is still on its way.
 *
 * Reduced motion has to raise the opacity explicitly: the keyframes rest at
 * 25%, so merely stopping the animation would leave three dots so faint they
 * read as nothing happening at all.
 */
function ThinkingIndicator() {
  return (
    <p className="flex gap-1.5 py-3" role="status">
      <span className="sr-only">Thinking</span>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          aria-hidden="true"
          style={{ animationDelay: `${index * 0.18}s` }}
          className="size-1.5 animate-thinking rounded-full bg-muted-foreground motion-reduce:animate-none motion-reduce:opacity-100"
        />
      ))}
    </p>
  );
}

const wordCount = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;
